/**
 * KPI tile — the only "big number" block in the app: value on top, label
 * below, an optional one-line note. The tone colours the value only.
 *
 * Pass `onClick` to make the tile a filter toggle (it then renders as a
 * <button> with `aria-pressed` = `selected`). No icons, arrows or accent
 * stripes: the number is the content.
 */

import { Children, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';

import type { KpiTone } from '../lib/types';

const TONE_CLASS: Readonly<Record<KpiTone, string>> = {
  neutral: '',
  good: 'good',
  warn: 'warn',
  alert: 'alert',
  critical: 'critical',
};

export interface KpiTileProps {
  /** German, sentence case. */
  label: string;
  value: ReactNode;
  tone?: KpiTone;
  /** One short line under the label, e.g. "im Produktkatalog". */
  note?: ReactNode;
  /** Native tooltip explaining how the number is computed. */
  title?: string;
  /** Makes the tile a toggle button. */
  onClick?: () => void;
  /** Pressed state of a toggle tile. */
  selected?: boolean;
  /** Overrides the button's accessible name (toggle tiles only). */
  ariaLabel?: string;
  /** id of the region the toggle filters (toggle tiles only). */
  controls?: string;
}

export function KpiTile({
  label,
  value,
  tone = 'neutral',
  note,
  title,
  onClick,
  selected,
  ariaLabel,
  controls,
}: KpiTileProps): ReactNode {
  const classes = ['kpi'];
  const toneClass = TONE_CLASS[tone];
  if (toneClass) classes.push(toneClass);
  const content = (
    <>
      <span className="kpi-value">{value}</span>
      <span className="kpi-label">{label}</span>
      {note ? <span className="kpi-note">{note}</span> : null}
    </>
  );

  if (onClick) {
    classes.push('interactive');
    if (selected) classes.push('selected');
    return (
      <button
        type="button"
        className={classes.join(' ')}
        title={title}
        aria-pressed={selected ?? false}
        aria-label={ariaLabel}
        aria-controls={controls}
        onClick={onClick}
      >
        {content}
      </button>
    );
  }

  return (
    <div className={classes.join(' ')} title={title}>
      {content}
    </div>
  );
}

/**
 * Column count the grid actually renders (auto-fit wraps on narrow screens).
 * global.css uses it as `data-cols` to round a selected tile's ring only at
 * the corners that meet the card's rounded corners.
 */
function useRenderedColumns(ref: { current: HTMLElement | null }, fallback: number): number {
  const [columns, setColumns] = useState(fallback);
  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return undefined;
    const measure = (): void => {
      // Collapsed auto-fit tracks report as 0px; count only real columns.
      const tracks = getComputedStyle(element).gridTemplateColumns.split(' ')
        .filter((track) => Number.parseFloat(track) > 0).length;
      if (tracks > 0) setColumns(tracks);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [ref]);
  return columns;
}

/** A tile description for `KpiRow`; `key` defaults to the label. */
export interface KpiItem extends KpiTileProps {
  key?: string;
}

export interface KpiRowProps {
  /** Tiles to render, in order. Alternatively pass <KpiTile> children. */
  counters?: readonly KpiItem[];
  children?: ReactNode;
  /** Accessible name of the group, e.g. "Kennzahlen". */
  label?: string;
}

/**
 * Responsive grid of tiles (`.kpis`). On wide screens all tiles share one row;
 * from seven tiles on they split into two even rows instead of leaving one or
 * two orphans on the second line.
 */
export function KpiRow({ counters, children, label = 'Kennzahlen' }: KpiRowProps): ReactNode {
  const count = (counters?.length ?? 0) + Children.count(children);
  const columns = count <= 6 ? count : Math.ceil(count / 2);
  const style = { '--kpi-cols': columns } as CSSProperties;
  const ref = useRef<HTMLElement>(null);
  const renderedColumns = useRenderedColumns(ref, columns);
  return (
    <section ref={ref} className="kpis" aria-label={label} style={style} data-cols={renderedColumns}>
      {counters?.map(({ key, ...tile }) => <KpiTile key={key ?? tile.label} {...tile} />)}
      {children}
    </section>
  );
}

export default KpiTile;
